import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import usePolicyVerification from '@v2/commons/hooks/policyVerification/usePolicyVerification'
import showModalNotificationError from '@v2/commons/modals/ModalNotification/showModalNotificationError'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'
import { getNetChangesBetweenObjects } from '@v2/commons/utils/objectManipulation'
import { transformUpdateTaskFormValues } from '@v2/domains/task/utils/valuesTransformer.util'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import {
  useGetEscalationByIdQuery,
  useGetEscalationFormTemplateQuery,
  useUpdateEscalationFormMutation,
  useUpdateEscalationMutation,
} from '../api/escalation.api'
import {
  EscalationStatusEnum,
  FormSubmissionDto,
  IEscalation,
  IFormField,
  IFormTemplate,
  MutateEscalationData,
} from '../escalation.types'

const IDG_FORM_TEMPLATE_ID = '2d9942d4-68cd-43bb-be84-dba43da47fb5'

const useUpdateEscalation = (escalationId: string) => {
  const [isReady, setIsReady] = useState(false)
  const {
    data: escalation,
    isSuccess: isEscalationQuerySuccess,
    isError,
    refetch,
  } = useGetEscalationByIdQuery(escalationId)

  const hasUpdateEscalationPolicy = usePolicyVerification(
    WbfPolicies.is_update_escalation_enabled,
  )

  const {
    data: escalationFormTemplate,
    isSuccess: isFormTemplateQuerySuccess,
  } = useGetEscalationFormTemplateQuery(IDG_FORM_TEMPLATE_ID)

  const [updateEscalation, updateEscalationMutationStatus] =
    useUpdateEscalationMutation()

  const [updateFormFiles, formFielsUpdateStatus] =
    useUpdateEscalationFormMutation()

  const validateNotEmptyOnSubmit = (
    value: MutateEscalationData,
    values: MutateEscalationData,
  ) => {
    if (values.formStatus === EscalationStatusEnum.SUBMITTED) {
      if (!value) {
        return 'This field must be filled in before submitting escalation!'
      }
      return null
    }
  }
  const updateEscalationForm = useForm<MutateEscalationData>({
    mode: 'uncontrolled',
    validate: {
      formFields: {
        summary: validateNotEmptyOnSubmit,
        troubleshooting: validateNotEmptyOnSubmit,
        startDate: validateNotEmptyOnSubmit,
      },
    },
    transformValues: (values) => {
      const {
        createdBy,
        createdAt,
        updatedAt,
        updatedBy,
        formFields,
        abc,
        benefit,
        ...rest
      } = values

      const formUpdatedFields = transFormEscalationData(formFields!)

      const netEscalationChanges = getNetChangesBetweenObjects<
        MutateEscalationData,
        IEscalation
      >(rest, {
        ...escalation,
        assignedToId: escalation?.assignedTo.id,
      } as IEscalation)
      return {
        ...netEscalationChanges,
        ...(!netEscalationChanges.formStatus &&
        rest.formStatus === EscalationStatusEnum.CREATED
          ? { formStatus: EscalationStatusEnum.DRAFT }
          : {}),
        form: formUpdatedFields,
      }
    },
  })

  const transFormEscalationData = (
    formFields: NonNullable<MutateEscalationData['formFields']>,
  ) => {
    const payload = escalation?.form
      ? (escalation.form?.payload as FormSubmissionDto['payload'])
      : (escalationFormTemplate?.payload as IFormTemplate['payload'])

    const updatedFields: IFormField[] = []

    payload?.steps[0].fields?.forEach((originalField) => {
      const fieldOverride = { ...originalField }
      fieldOverride.value = formFields[fieldOverride.name]
      updatedFields.push(fieldOverride)
    })

    return {
      templateId: escalationFormTemplate?.id,
      ...(escalation?.form ?? {}),
      payload: { steps: [{ fields: updatedFields }] },
    }
  }

  const getFormEscalationData = () => {
    const processEscalation = escalation?.form
      ? (escalation.form as FormSubmissionDto)
      : (escalationFormTemplate as IFormTemplate)

    return processEscalation?.payload?.steps
      .flatMap((step) => step.fields)
      .reduce((acc, current) => {
        if (current.type === 'datePicker') {
          acc[current.name] = dayjs(current.value as any).isValid()
            ? dayjs(current.value as any).toDate()
            : null
        } else {
          acc[current.name] = current.value
        }
        return acc
      }, {} as NonNullable<MutateEscalationData['formFields']>)
  }

  useEffect(() => {
    if (isError) {
      showModalNotificationError({
        subtitle: 'Error fetching escalation information',
        onCancel: () => modals.closeAll(),
      })
    } else if (isEscalationQuerySuccess && isFormTemplateQuerySuccess) {
      const { createdAt, updatedAt, assignedToId } =
        transformUpdateTaskFormValues(escalation)
      const formFields = getFormEscalationData()
      updateEscalationForm.setValues({
        formStatus: escalation.formStatus,
        benefitId: escalation.benefitId,
        abc: escalation.abc,
        abcId: escalation.abcId,
        issue: escalation.issue,
        priority: escalation.priority,
        createdBy: escalation.createdBy,
        updatedBy: escalation.updatedBy,
        benefit: escalation.benefit,
        createdAt,
        updatedAt,
        assignedToId,
        formFields,
      })
      setIsReady(true)
    }
  }, [isError, isEscalationQuerySuccess, isFormTemplateQuerySuccess])

  const onSubmitUpdate = () => {
    updateEscalationForm.onSubmit(async (values) => {
      return updateEscalation({
        id: escalationId,
        data: values,
      })
        .unwrap()
        .then(() => {
          notifications.show({
            title: 'Success!',
            message: 'Successfully updated escalation!',
            color: 'green',
          }),
            modals.closeAll()
        })
        .catch((error) => {
          handleMutationError(error, 'Failed to update Escalation!')
          refetch()
        })
    })()
  }

  const updateFormDocuments = () => {
    const { form } = updateEscalationForm.getTransformedValues()
    const filesPayload = form!.payload!.steps[0].fields.find(
      (field) => field.name === 'files',
    )
    const originalForm = escalation?.form
      ? (escalation.form as FormSubmissionDto)
      : (({ id, ...rest } = escalationFormTemplate as IFormTemplate) => rest)(
          escalationFormTemplate as IFormTemplate,
        )
    const updatedFields = originalForm.payload!.steps[0].fields.map((field) => {
      if (field.name === 'files') {
        return filesPayload as IFormField
      }
      return field
    })
    const originalFormCopy: FormSubmissionDto = JSON.parse(
      JSON.stringify(originalForm),
    )
    originalFormCopy.templateId = IDG_FORM_TEMPLATE_ID
    originalFormCopy.payload!.steps[0].fields = updatedFields!
    const data: { form: FormSubmissionDto; personId: string } = {
      personId: escalation!.person.id,
      form: originalFormCopy as FormSubmissionDto,
    }
    return updateFormFiles({ id: escalationId, data })
  }

  const onCloseWithoutSave = () => {
    updateFormDocuments()
      .unwrap()
      .then(() => modals.closeAll())
  }

  const isSubmittedForm =
    !!escalation?.formStatus &&
    ![EscalationStatusEnum.CREATED, EscalationStatusEnum.DRAFT].includes(
      escalation!.formStatus,
    )

  return {
    escalation,
    updateEscalationForm,
    isEscalationQuerySuccess: isReady,
    onSubmitUpdate,
    updateEscalationMutationStatus,
    isSubmittedForm,
    hasUpdateEscalationPolicy,
    chatId: escalation?.chat?.id,
    onCloseWithoutSave,
    formFielsUpdateStatus: formFielsUpdateStatus.status,
  }
}

export default useUpdateEscalation
